import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Camera,
  Users,
  Settings,
  Key,
  Trash2,
  PencilIcon,
  Shield,
  ChevronRight,
  Building2,
  Loader2,
  Check,
  X,
  Circle,
  Plus,
  AlertTriangle,
  UserPlus,
} from 'lucide-react';
import api, {
  getClinicInfo,
  deleteUser,
  changeUserPassword,
  getTeams,
  Team,
} from '../services/api.ts';
import { listProfileUsers as listUsers, createProfileUser as createUser, updateProfileUser as updateUser, resendUserAccess, resendPasswordSetup, passwordSetupDeliveryLabel, getUserAccessCapabilities, credentialDeliveryLabel, userProfileError, type ProfileUser as User, type ProfileUserCreate as UserCreate, type UserAccessCapabilities } from '../services/apiUserProfile.ts';
import { ProfilePhotoInput } from '../components/profile/ProfilePhotoInput.tsx';
import TeamsManagement from '../components/TeamsManagement.tsx';
import { useTheme } from '../contexts/ThemeContext.tsx';

// Tipos/Interfaces
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'primary' | 'secondary' | 'destructive' | 'ghost';
  children: React.ReactNode;
}

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  children: React.ReactNode;
}

interface TabsProps {
  children: React.ReactNode;
}

interface TabsListProps {
  children: React.ReactNode;
}


interface TabsContentProps {
  value: string;
  currentValue: string;
  children: React.ReactNode;
}

interface DialogProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: 'md' | 'lg';
}

interface DropdownMenuProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
}

interface DropdownItemProps {
  onClick?: () => void;
  icon?: React.ReactNode;
  destructive?: boolean;
  children: React.ReactNode;
}


interface UserFormData {
  show_name_in_chat: boolean;
  name: string;
  email: string;
  role: string;
  team_id?: number;
  photo_url: string | null;
  password: string;
  confirm_password: string;
}

const CONTACT_STAGE_LABELS: Record<string, string> = {
  all: 'Todos os contatos',
  lead: 'Leads',
  agendado: 'Agendados',
  perdido: 'Perdidos',
  faltou: 'No-show',
  compareceu: 'Compareceu',
  venda: 'Venda',
  paciente: 'Pacientes',
  contato: 'Contatos avulsos',
};

const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrador',
  manager: 'Gerente',
  staff: 'Funcionário',
};

const getRoleLabel = (role: string) => ROLE_LABELS[role] || role;

const getTeamContactSummary = (team?: Team): string => {
  const stages = team?.contact_funnel_stages || [];
  if (stages.includes('all')) {
    return 'Todos os contatos';
  }
  if (stages.length === 0) {
    return 'Sem lista de contatos';
  }
  return stages.map(stage => CONTACT_STAGE_LABELS[stage] || stage).join(', ');
};

const getTeamMenuSummary = (team?: Team): string => {
  const count = team?.menu_permissions?.length || 0;
  if (count === 0) {
    return 'Sem menus liberados';
  }
  return `${count} ${count === 1 ? 'permissão' : 'permissões'} de menu`;
};

// ------------------
//  Componentes Base
// ------------------


export const CardHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <div className="mb-3">{children}</div>;
};

export const CardTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <h2 className="text-xl font-bold text-gray-900">{children}</h2>;
};

export const CardContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <div>{children}</div>;
};

/**
 * Button
 * (Cores base trocadas para #1F2937)
 */
const Button: React.FC<ButtonProps> = ({
  variant = 'default',
  className = '',
  children,
  ...props
}) => {
  const { isDark } = useTheme();
  const baseClasses = `
    inline-flex items-center justify-center rounded-2xl text-sm font-medium
    transition-all duration-300 ease-out focus:outline-none focus:ring-2
    disabled:opacity-50 disabled:pointer-events-none active:scale-95
  `;

  const variants = {
    default:
      isDark
        ? 'bg-gray-700 border border-gray-600 text-gray-200 hover:bg-gray-600 focus:ring-sminy-primary/20'
        : 'bg-white border border-gray-200 text-gray-900 hover:bg-gray-50 focus:ring-sminy-primary/20',
    primary:
      'bg-sminy-primary text-white font-medium hover:bg-sminy-primary-hover focus:ring-sminy-primary/20',
    secondary:
      isDark
        ? 'bg-gray-700 text-gray-200 hover:bg-gray-600 focus:ring-sminy-primary/20'
        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-sminy-primary/20',
    destructive:
      'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500/40',
    ghost:
      isDark
        ? 'text-gray-300 hover:bg-gray-700 focus:ring-sminy-primary/20'
        : 'hover:bg-gray-100 focus:ring-sminy-primary/20',
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} px-4 py-2 ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

/**
 * Input
 * (focus:border e focus:ring trocados para #1F2937)
 */
const Input: React.FC<InputProps> = ({ label, error, className = '', ...props }) => {
  const { isDark } = useTheme();
  return (
    <div className="flex flex-col space-y-1">
      {label && <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{label}</label>}
      <input
        className={`
          block w-full rounded-xl border px-4 py-3 text-sm
          shadow-sm placeholder:text-gray-400 focus:border-sminy-primary focus:outline-none
          focus:ring-2 focus:ring-sminy-primary/20 transition-all duration-300
          ${isDark ? 'border-gray-700 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-900'}
          ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''}
          ${className}
        `}
        {...props}
      />
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};

/**
 * Select
 * (focus:border e focus:ring trocados para #1F2937)
 */
const Select: React.FC<SelectProps> = ({ label, error, className = '', children, ...props }) => {
  const { isDark } = useTheme();
  return (
    <div className="flex flex-col space-y-1">
      {label && <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{label}</label>}
      <select
        className={`
          block w-full rounded-xl border px-4 py-3 text-sm
          shadow-sm focus:border-sminy-primary focus:outline-none focus:ring-2 focus:ring-sminy-primary/20 transition-all duration-300
          ${isDark ? 'border-gray-700 bg-gray-900 text-white' : 'border-gray-200 bg-white text-gray-900'}
          ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''}
          ${className}
        `}
        {...props}
      >
        {children}
      </select>
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};

/**
 * Tabs
 */
const Tabs: React.FC<TabsProps> = ({ children }) => {
  return <div className="flex flex-col space-y-4">{children}</div>;
};

export const TabsList: React.FC<TabsListProps> = ({ children }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
      {children}
    </div>
  );
};

// Component TabsTrigger is no longer needed as we use direct cards now

const TabsContent: React.FC<TabsContentProps> = ({ value, currentValue, children }) => {
  if (value !== currentValue) return null;
  return <div className="space-y-4">{children}</div>;
};

/**
 * Dialog (Modal)
 */
const Dialog: React.FC<DialogProps> = ({ open, onClose, children, size = 'md' }) => {
  const { isDark } = useTheme();
  if (!open) return null;

  const widthClass = size === 'lg' ? 'max-w-3xl' : 'max-w-lg';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className={`relative z-[10000] w-full ${widthClass} rounded-2xl p-6 shadow-xl animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto ${
        isDark ? 'bg-gray-800' : 'bg-white'
      }`}>
        {children}
      </div>
    </div>
  );
};

export const DialogHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <div className="mb-4">{children}</div>;
};

export const DialogTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isDark } = useTheme();
  return <h3 className={`text-lg font-semibold leading-none tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>{children}</h3>;
};

export const DialogFooter: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">{children}</div>;
};

/**
 * Dropdown Menu
 */
const DropdownMenu: React.FC<DropdownMenuProps> = ({ trigger, children }) => {
  const { isDark } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = React.useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        left: rect.right - 176 // 176px = w-44
      });
    }
    setIsOpen((prev) => !prev);
  };

  const dropdownContent = isOpen ? (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 z-[9997]" 
        onClick={() => setIsOpen(false)}
      />
      {/* Dropdown */}
      <div
        className={`fixed z-[9998] w-44 rounded-xl border shadow-xl ${
          isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
        }`}
        style={{ 
          top: `${position.top}px`, 
          left: `${position.left}px`
        }}
        role="menu"
      >
        <div className="p-1" onClick={() => setIsOpen(false)}>
          {children}
        </div>
      </div>
    </>
  ) : null;

  return (
    <>
      <div className="relative inline-block text-left" ref={triggerRef}>
        <div onClick={handleToggle}>{trigger}</div>
      </div>
      {dropdownContent && createPortal(dropdownContent, document.body)}
    </>
  );
};

const DropdownItem: React.FC<DropdownItemProps> = ({
  onClick,
  icon,
  destructive,
  children,
}) => {
  const { isDark } = useTheme();

  return (
    <button
      onClick={onClick}
      className={`
        group flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm
        focus:outline-none transition-all duration-300
        ${isDark ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}
        ${destructive ? (isDark ? 'text-red-300 hover:bg-red-500/10' : 'text-red-600 hover:bg-red-50') : ''}
      `}
    >
      {icon && <span className="h-4 w-4">{icon}</span>}
      <span>{children}</span>
    </button>
  );
};

// ------------------
//  Componente Final
// ------------------
const ClinicConfig: React.FC = () => {
  // ------------------
  // Hook de Tema - Deve vir primeiro
  // ------------------
  const { isDark } = useTheme();
  
  // ------------------
  // Estados da Clínica
  // ------------------
  const [currentTab, setCurrentTab] = useState('users');
  const [nameClinic, setNameClinic] = useState('');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [clinicLogo, setClinicLogo] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Controla se o formulário da clínica está em modo de edição
  const [isClinicEditing, setIsClinicEditing] = useState(false);

  // Guardamos o valor original para reverter se cancelar
  const [originalName, setOriginalName] = useState('');
  const [originalLogoUrl, setOriginalLogoUrl] = useState<string | null>(null);

  // ------------------
  // Estados dos Usuários
  // ------------------
  const [users, setUsers] = useState<User[]>([]);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | 'resend' | 'password' | 'setup'>('create');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userPendingDelete, setUserPendingDelete] = useState<User | null>(null);
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [userForm, setUserForm] = useState<UserFormData>({
    name: '',
    email: '',
    role: 'staff',
    photo_url: null,
    show_name_in_chat: false,
    password: '',
    confirm_password: '',
  });

  // ------------------
  // Estados das Equipes
  // ------------------
  const [teams, setTeams] = useState<Team[]>([]);
  const teamById = useMemo(() => {
    return teams.reduce<Record<number, Team>>((acc, team) => {
      acc[team.id] = team;
      return acc;
    }, {});
  }, [teams]);
  const selectedTeamForUserForm = userForm.team_id ? teamById[userForm.team_id] : undefined;
  
  const [accessCapabilities, setAccessCapabilities] = useState<UserAccessCapabilities | null>(null);
  const canManageUsers = Boolean(accessCapabilities?.enabled || accessCapabilities?.profiles_enabled);
  const [capabilitiesError, setCapabilitiesError] = useState(false);
  const [capabilitiesRetry, setCapabilitiesRetry] = useState(0);
  const [userOperationError, setUserOperationError] = useState('');
  const [userNotice, setUserNotice] = useState('');
  const [isSavingUser, setIsSavingUser] = useState(false);
  const [readingUserPhoto, setReadingUserPhoto] = useState(false);
  const savingUserRef = useRef(false);
  useEffect(() => {
    let active = true;
    setAccessCapabilities(null); setCapabilitiesError(false);
    getUserAccessCapabilities().then(value => { if (active) setAccessCapabilities(value); }).catch(() => { if (active) setCapabilitiesError(true); });
    return () => { active = false; };
  }, [capabilitiesRetry]);

  // ------------------
  //  Efeitos
  // ------------------
  useEffect(() => {
    fetchClinicData();
    fetchUsers();
    fetchTeams();
  }, []);

  const fetchClinicData = async () => {
    try {
      const info = await getClinicInfo();
      const clinicName = info.name_clinic || '';
      const clinicLogoUrl = info.logo_url || null;

      setNameClinic(clinicName);
      setLogoUrl(clinicLogoUrl);

      // Guardamos no "original" para referencia
      setOriginalName(clinicName);
      setOriginalLogoUrl(clinicLogoUrl);

      // Se já tem nome ou logo, começamos "bloqueado", senão começamos editando
      if (clinicName || clinicLogoUrl) {
        setIsClinicEditing(false);
      } else {
        setIsClinicEditing(true);
      }

      setIsLoading(false);
    } catch (error) {
      console.error('Erro ao carregar dados da clínica:', error);
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const clinicId = Number(localStorage.getItem('clinic_id'));
      const response = await listUsers(clinicId);
      setUsers(response);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await getTeams();
      setTeams(response);
    } catch (error) {
      console.error('Erro ao carregar equipes:', error);
    }
  };

  // ------------------
  //  Handlers Clínica
  // ------------------
  const handleClinicSave = async () => {
    try {
      const formData = new FormData();
      formData.append('name_clinic', nameClinic);
      if (clinicLogo) {
        formData.append('logo', clinicLogo);
      }

      const resp = await api.put('/api/clinic', formData);

      // Se subiu um novo logo, atualizamos o logoUrl
      if (clinicLogo) {
        setLogoUrl(resp.data.logoUrl);
      }

      // Depois de salvar, atualizamos os valores originais e bloqueamos novamente
      setOriginalName(nameClinic);
      setOriginalLogoUrl(resp.data.logoUrl || logoUrl);
      setClinicLogo(null);
      setIsClinicEditing(false);

    } catch (error) {
      console.error('Erro ao atualizar clínica:', error);
    }
  };

  const handleCancelClinicEdit = () => {
    // Reverte para o valor original
    setNameClinic(originalName);
    setLogoUrl(originalLogoUrl);
    setClinicLogo(null);
    setIsClinicEditing(false);
  };

  // ------------------
  //  Handlers Usuário
  // ------------------
  const handleUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); if (savingUserRef.current || readingUserPhoto || !accessCapabilities || !canManageUsers) return;
    savingUserRef.current = true; setIsSavingUser(true); setUserOperationError('');
    try {
      if (!accessCapabilities.enabled && (dialogMode === 'create' || dialogMode === 'password')) {
        if (!userForm.password || userForm.password !== userForm.confirm_password) throw new Error('Senhas não conferem.');
        if (new Blob([userForm.password]).size > 72) throw new Error('A senha é muito longa. Reduza seu tamanho.');
      }
      let saved: User;
      if (dialogMode === 'password' && selectedUser) {
        if (accessCapabilities.enabled) throw new Error('Use Reenviar acesso neste modo.');
        saved = await changeUserPassword(selectedUser.id, userForm.password, userForm.confirm_password);
      } else if (dialogMode === 'setup' && selectedUser) {
        if (!selectedUser.is_active || !selectedUser.password_setup_required) return;
        saved = await resendPasswordSetup(selectedUser.id);
      } else if (dialogMode === 'resend' && selectedUser) {
        saved = await resendUserAccess(selectedUser.id);
      } else {
        const clinicId = Number(localStorage.getItem('clinic_id'));
        if (!clinicId) throw new Error('Selecione uma clínica válida antes de salvar o usuário.');
        const data: UserCreate = { name: userForm.name.trim(), email: userForm.email.trim(), role: userForm.role, clinic_id: clinicId, team_id: userForm.team_id ?? null, ...(accessCapabilities?.enabled ? { photo_url: userForm.photo_url } : {}), ...(accessCapabilities.chat_signature_available === true ? { show_name_in_chat: userForm.show_name_in_chat } : {}) };
        if (dialogMode === 'create') {
          if (!accessCapabilities.enabled) {data.password = userForm.password; data.confirm_password = userForm.confirm_password;}
          const result = await createUser(data);
          if ('invitation_pending' in result) {
            setUserNotice(result.message);
            setShowUserDialog(false); resetUserForm();
            return;
          }
          saved = result;
        }
        else { const { clinic_id, ...updates } = data; saved = await updateUser(selectedUser!.id,
          selectedUser!.identity_managed_by_user ? { role: data.role, team_id: data.team_id } : updates); }
      }
      setUsers(prev => prev.some(user => user.id === saved.id) ? prev.map(user => user.id === saved.id ? saved : user) : [...prev, saved]);
      setUserNotice(saved.password_setup_required ? `Usuário atualizado. ${passwordSetupDeliveryLabel(saved.password_setup_delivery)}.` : dialogMode === 'password' ? 'Senha alterada.' : dialogMode === 'edit' ? 'Usuário atualizado.' : accessCapabilities.enabled ? `Usuário salvo. ${credentialDeliveryLabel(saved.credential_delivery)}.` : 'Usuário salvo.');
      setShowUserDialog(false); resetUserForm();
      void fetchUsers();
    } catch (error) { setUserOperationError(userProfileError(error, 'Não foi possível salvar o usuário.')); }
    finally { savingUserRef.current = false; setIsSavingUser(false); }
  };

  const handleDeleteUser = async () => {
    if (!userPendingDelete) return;
    setIsDeletingUser(true);
    try {
      await deleteUser(userPendingDelete.id);
      await fetchUsers();
      setUserPendingDelete(null);
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
    } finally {
      setIsDeletingUser(false);
    }
  };

  const resetUserForm = () => {
    setUserForm({
      name: '',
      email: '',
      role: 'staff',
      team_id: undefined,
      photo_url: null,
      show_name_in_chat: false,
      password: '',
      confirm_password: '',
    });
    setSelectedUser(null);
    setUserOperationError('');
  };

  const openCreateUserDialog = () => {
    if (!accessCapabilities || !canManageUsers) return;
    resetUserForm();
    setDialogMode('create');
    setShowUserDialog(true);
  };

  const openEditUserDialog = (user: User) => {
    if (!accessCapabilities || !canManageUsers) return;
    setSelectedUser(user);
    setUserForm({
      name: user.name,
      email: user.email,
      role: user.role,
      team_id: user.team_id,
      photo_url: user.photo_url || null,
      show_name_in_chat: user.show_name_in_chat === true,
      password: '',
      confirm_password: '',
    });
    setDialogMode('edit');
    setShowUserDialog(true);
  };

  const openResendUserDialog = (user: User) => {
    if (!accessCapabilities?.enabled) return;
    setSelectedUser(user);
    setUserForm({
      name: user.name,
      email: user.email,
      role: user.role,
      team_id: user.team_id,
      photo_url: user.photo_url || null,
      show_name_in_chat: user.show_name_in_chat === true,
      password: '',
      confirm_password: '',
    });
    setDialogMode('resend');
    setShowUserDialog(true);
  };

  const openSetupDialog = (user: User) => {
    if (!canManageUsers || !user.is_active || !user.password_setup_required) return;
    resetUserForm(); setSelectedUser(user); setDialogMode('setup'); setShowUserDialog(true);
  };

  const openPasswordDialog = (user: User) => {
    if (!canManageUsers || !accessCapabilities || accessCapabilities.enabled || user.must_change_password || user.password_setup_required) return;
    resetUserForm();
    setSelectedUser(user);
    setDialogMode('password');
    setShowUserDialog(true);
  };

  const handleManageTeamsFromUserDialog = () => {
    setShowUserDialog(false);
    resetUserForm();
    setCurrentTab('teams');
  };
  
  const getTeamColor = (teamCode: string) => {
    const colors = {
      CRC: "bg-blue-100 text-blue-800",
      VENDAS: "bg-green-100 text-green-800",
      GERENTE: "bg-gray-100 text-gray-800",
      SUPORTE: "bg-purple-100 text-purple-800"
    };
    return colors[teamCode as keyof typeof colors] || "bg-gray-100 text-gray-600";
  };

  const renderTeamAccessPreview = (team?: Team) => {
    if (!team) {
      return (
        <div className={`rounded-xl border p-4 ${
          isDark ? 'border-amber-500/30 bg-amber-500/10' : 'border-amber-200 bg-amber-50'
        }`}>
          <div className="flex items-start gap-3">
            <div className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${
              isDark ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700'
            }`}>
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div>
              <p className={`text-sm font-semibold ${isDark ? 'text-amber-100' : 'text-amber-900'}`}>Sem equipe selecionada</p>
              <p className={`mt-1 text-xs leading-5 ${isDark ? 'text-amber-100/80' : 'text-amber-800'}`}>
                O usuário não herda as permissões personalizadas de uma equipe. Para controlar menus, contatos e chat, vincule uma equipe.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-900/40' : 'border-slate-200 bg-slate-50'}`}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{team.name}</p>
            <p className={`mt-1 text-xs leading-5 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
              {team.description || 'Equipe personalizada'}
            </p>
          </div>
          <span className={`w-fit rounded-full px-2.5 py-1 text-xs font-semibold ${getTeamColor(team.code)}`}>
            {team.user_count || 0} membros
          </span>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className={`rounded-lg border px-3 py-2 ${isDark ? 'border-gray-700 bg-gray-800/70' : 'border-slate-200 bg-white'}`}>
            <p className={`text-[11px] font-semibold uppercase ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>Menus</p>
            <p className={`mt-1 text-sm font-medium ${isDark ? 'text-gray-200' : 'text-slate-800'}`}>{getTeamMenuSummary(team)}</p>
          </div>
          <div className={`rounded-lg border px-3 py-2 ${isDark ? 'border-gray-700 bg-gray-800/70' : 'border-slate-200 bg-white'}`}>
            <p className={`text-[11px] font-semibold uppercase ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>Contatos</p>
            <p className={`mt-1 text-sm font-medium ${isDark ? 'text-gray-200' : 'text-slate-800'}`}>{getTeamContactSummary(team)}</p>
          </div>
        </div>
      </div>
    );
  };

  // ------------------
  //  Render
  // ------------------
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 text-sminy-primary animate-spin" />
          <p className="text-lg font-medium text-gray-700">Carregando configurações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-6 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header Card estilo HubSpot */}
      <div className={`relative rounded-2xl border p-6 shadow-sm mb-6 max-w-7xl mx-auto ${isDark ? 'border-gray-700 bg-gray-800' : 'border-slate-200 bg-white'}`}>
        <div className="flex items-center gap-4">
          <div className="relative w-14 h-14">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="w-14 h-14 rounded-2xl object-cover" />
            ) : (
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-[#111827] to-sminy-primary flex items-center justify-center">
                <span className="text-white text-2xl font-semibold">S</span>
              </div>
            )}
            {isClinicEditing && (
              <label className="absolute inset-0 rounded-2xl bg-black/50 flex items-center justify-center cursor-pointer hover:bg-black/60 transition">
                <Camera className="w-6 h-6 text-white" />
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => setClinicLogo(e.target.files?.[0] || null)}
                />
              </label>
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              {isClinicEditing ? (
                <input 
                  type="text" 
                  value={nameClinic}
                  onChange={(e) => setNameClinic(e.target.value)}
                  className={`text-xl md:text-2xl font-semibold px-3 py-1 rounded-lg border transition-all duration-300 ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:border-sminy-primary' : 'bg-gray-50 border-gray-200 text-slate-900 focus:border-sminy-primary'} focus:ring-2 focus:ring-sminy-primary/20 focus:outline-none`}
                  placeholder="Nome da clínica"
                />
              ) : (
                <h1 className={`text-xl md:text-2xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{nameClinic || 'Sminy.ai'}</h1>
              )}
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-100">
                <Circle className="w-3 h-3 fill-emerald-500 text-emerald-500" /> Sistema Ativo
              </span>
            </div>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Configurações gerais da clínica</p>
          </div>
          <div className="flex gap-2">
            {isClinicEditing ? (
              <>
                <button 
                  onClick={handleCancelClinicEdit}
                  className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium shadow-sm hover:shadow transition ${isDark ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600' : 'border-slate-200 bg-white text-slate-700'}`}
                >
                  <X className="w-4 h-4" /> Cancelar
                </button>
                <button 
                  onClick={handleClinicSave}
                  className="inline-flex items-center gap-2 rounded-xl bg-sminy-primary px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-sminy-primary-hover transition"
                >
                  <Check className="w-4 h-4" /> Salvar
                </button>
              </>
            ) : (
              <button 
                onClick={() => setIsClinicEditing(true)}
                className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium shadow-sm hover:shadow transition ${isDark ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600' : 'border-slate-200 bg-white text-slate-700'}`}
              >
                <PencilIcon className="w-4 h-4" /> Editar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Navigation Cards estilo Teams */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 max-w-7xl mx-auto">
        {/* Card Usuários */}
        <div 
          className={`
            relative p-5 rounded-2xl border cursor-pointer transition hover:-translate-y-0.5 hover:shadow-md group
            ${currentTab === 'users' 
              ? isDark 
                ? 'border-sminy-primary bg-sminy-primary/10'
                : 'border-sminy-primary bg-blue-50'
              : isDark
                ? 'border-gray-700 bg-gray-800'
                : 'border-slate-200 bg-white'
            }
          `}
          onClick={() => setCurrentTab('users')}
        >
          <div className="flex items-start gap-4">
            <div className={`grid place-items-center rounded-xl w-10 h-10 ${
              currentTab === 'users'
                ? 'text-sminy-primary bg-sminy-primary/10'
                : isDark 
                  ? 'text-gray-400 bg-gray-700/50'
                  : 'text-gray-500 bg-slate-100'
            }`}>
              <Users className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Usuários</h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Gerencie sua equipe</p>
              <div className="mt-3 flex items-center gap-2 text-sm">
                <span className={`${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Total de usuários:</span>
                <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{users.length}</span>
              </div>
            </div>
            <ChevronRight className={`w-5 h-5 group-hover:text-slate-400 ${isDark ? 'text-gray-500' : 'text-slate-300'}`} />
          </div>
        </div>

        {/* Card Equipes */}
        <div 
          className={`
            relative p-5 rounded-2xl border cursor-pointer transition hover:-translate-y-0.5 hover:shadow-md group
            ${currentTab === 'teams' 
              ? isDark 
                ? 'border-sminy-primary bg-sminy-primary/10'
                : 'border-sminy-primary bg-blue-50'
              : isDark
                ? 'border-gray-700 bg-gray-800'
                : 'border-slate-200 bg-white'
            }
          `}
          onClick={() => setCurrentTab('teams')}
        >
          <div className="flex items-start gap-4">
            <div className={`grid place-items-center rounded-xl w-10 h-10 ${
              currentTab === 'teams'
                ? 'text-sminy-primary bg-sminy-primary/10'
                : isDark 
                  ? 'text-gray-400 bg-gray-700/50'
                  : 'text-gray-500 bg-slate-100'
            }`}>
              <Shield className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Equipes</h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Organize departamentos</p>
              <div className="mt-3 flex items-center gap-2 text-sm">
                <span className={`${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Total de equipes:</span>
                <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{teams.length}</span>
              </div>
            </div>
            <ChevronRight className={`w-5 h-5 group-hover:text-slate-400 ${isDark ? 'text-gray-500' : 'text-slate-300'}`} />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto mt-8">
        <Tabs>

        {/* Conteúdo da aba "Clínica" */}
        <TabsContent value="clinic" currentValue={currentTab}>
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gradient-to-r from-[#111827] to-sminy-primary rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-sminy-primary/20">
              <Settings className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Configurações da Clínica</h2>
            <p className="text-gray-600 mb-8">Use o header acima para editar as informações básicas da sua clínica.</p>
            <div className="bg-gradient-to-r from-sminy-primary/10 to-sminy-primary/5 rounded-2xl p-6 max-w-md mx-auto">
              <Building2 className="w-8 h-8 text-sminy-primary mx-auto mb-2" />
              <p className="text-sm text-gray-700 font-medium">Nome: {nameClinic || 'Não definido'}</p>
              <p className="text-xs text-gray-500 mt-1">Logo: {logoUrl ? 'Configurado' : 'Não definido'}</p>
            </div>
          </div>
        </TabsContent>

        {/* Conteúdo da aba "Usuários" estilo HubSpot */}
        <TabsContent value="users" currentValue={currentTab}>
          <section id="users">
            {userNotice && <p role="status" className="mb-3 text-sm text-sminy-primary">{userNotice}</p>}
            {accessCapabilities && !canManageUsers && <p role="status" className="mb-3 text-sm text-amber-600">A configuração de usuários ainda não está disponível. <button type="button" className="underline" onClick={() => setCapabilitiesRetry(value => value + 1)}>Atualizar configuração</button></p>}
            {capabilitiesError && <p role="alert" className="mb-3 text-sm text-amber-600">Não foi possível confirmar o modo de acesso dos usuários. <button type="button" className="underline" onClick={() => setCapabilitiesRetry(value => value + 1)}>Tentar novamente</button></p>}
            <button type="button" onClick={fetchUsers} className="mb-3 text-sm text-sminy-primary">Atualizar usuários e entregas</button>
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Usuários</h2>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                  Usuários entram em uma equipe e herdam os menus, contatos e acessos configurados nela.
                </p>
              </div>
              <button
                disabled={!canManageUsers}
                onClick={openCreateUserDialog}
                className="inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-white shadow-sm hover:shadow transition bg-sminy-primary hover:bg-sminy-primary-hover"
              >
                <UserPlus className="w-4 h-4" /> Novo usuário
              </button>
            </div>

            <div className={`overflow-x-auto rounded-2xl border shadow-sm ${isDark ? 'border-gray-700 bg-gray-800' : 'border-slate-200 bg-white'}`}>
              <table className="w-full min-w-[720px]">
                <thead className={`${isDark ? 'bg-gray-700/50' : 'bg-slate-50'}`}>
                  <tr className={`text-left text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                    <th className="px-5 py-3 font-medium">Usuário</th>
                    <th className="px-5 py-3 font-medium">Função</th>
                    <th className="px-5 py-3 font-medium">Equipe</th>
                    <th className="px-5 py-3 font-medium">Acesso</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-5 py-10">
                        <div className="flex flex-col items-center justify-center text-center gap-2 py-8">
                          <div className={`grid place-items-center w-12 h-12 rounded-xl ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-slate-100 text-slate-500'}`}>
                            <Users className="w-5 h-5" />
                          </div>
                          <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Nenhum usuário ainda</p>
                          <p className={`text-sm max-w-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Convide pessoas da sua clínica e vincule cada uma a uma equipe para controlar acessos.</p>
                          <button 
                            disabled={!canManageUsers}
                            onClick={openCreateUserDialog}
                            className={`mt-2 inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium shadow-sm hover:shadow transition ${isDark ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`}
                          >
                            <Plus className="w-4 h-4" /> Adicionar usuário
                          </button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => {
                      return (
                        <tr key={user.id} className={`border-t text-sm transition-colors ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-700/30' : 'border-slate-100 text-slate-700 hover:bg-slate-50'}`}>
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium text-sm ${
                                isDark
                                  ? 'bg-gray-700 text-gray-200'
                                  : 'bg-slate-200 text-slate-700'
                              }`}>
                                {user.photo_url ? <img src={user.photo_url} alt="" className="h-10 w-10 rounded-full object-cover" /> : user.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{user.name}</p>
                                <p className={`truncate text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>{user.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-4">
                            <span className="px-3 py-1 text-xs font-bold rounded-full bg-sminy-primary/10 text-sminy-primary">
                              {getRoleLabel(user.role)}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            {user.team ? (
                              <span className={`px-3 py-1 text-xs font-bold rounded-full ${getTeamColor(user.team.code)}`}>
                                {user.team.name}
                              </span>
                            ) : (
                              <span className={`text-xs italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Sem equipe</span>
                            )}
                          </td>
                          <td className="px-5 py-4"><p className="text-xs">{user.password_setup_required ? passwordSetupDeliveryLabel(user.password_setup_delivery) : credentialDeliveryLabel(user.credential_delivery)}</p>{user.password_setup_required && <p className="mt-1 text-xs">Definição de senha pendente</p>}{user.password_setup_required && user.password_setup_delivery?.expires_at && <p className="mt-1 text-xs">Link válido até {new Date(user.password_setup_delivery.expires_at).toLocaleString('pt-BR')}</p>}{user.must_change_password && <p className="mt-1 text-xs text-sminy-primary">Troca de senha pendente</p>}{user.must_change_password && user.credential_delivery?.expires_at && <p className="mt-1 text-xs">Válido até {new Date(user.credential_delivery.expires_at).toLocaleString('pt-BR')}</p>}</td>
                          <td className="px-5 py-4 text-right">
                            <DropdownMenu
                              trigger={
                                <button className={`rounded-lg border px-2.5 py-1.5 text-xs transition ${isDark ? 'border-gray-600 hover:bg-gray-700' : 'border-slate-200 hover:bg-slate-50'}`}>
                                  Gerenciar
                                </button>
                              }
                            >
                              {canManageUsers && <DropdownItem
                                icon={<PencilIcon className="h-4 w-4" />}
                                onClick={() => openEditUserDialog(user)}
                              >
                                Editar
                              </DropdownItem>}
                              {canManageUsers && !user.identity_managed_by_user && user.is_active && user.password_setup_required && <DropdownItem icon={<Key className="h-4 w-4" />} onClick={() => openSetupDialog(user)}>Reenviar link para definir senha</DropdownItem>}
                              {!user.identity_managed_by_user && accessCapabilities?.enabled && !user.password_setup_required && <DropdownItem
                                icon={<Key className="h-4 w-4" />}
                                onClick={() => openResendUserDialog(user)}
                              >
                                Reenviar acesso
                              </DropdownItem>}
                              {canManageUsers && !user.identity_managed_by_user && accessCapabilities && !accessCapabilities.enabled && !user.must_change_password && !user.password_setup_required && <DropdownItem icon={<Key className="h-4 w-4" />} onClick={() => openPasswordDialog(user)}>Alterar senha</DropdownItem>}
                              <DropdownItem
                                icon={<Trash2 className="h-4 w-4" />}
                                destructive
                                onClick={() => setUserPendingDelete(user)}
                              >
                                Excluir
                              </DropdownItem>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </TabsContent>

        {/* Conteúdo da aba "Equipes" */}
        <TabsContent value="teams" currentValue={currentTab}>
          <TeamsManagement />
        </TabsContent>
        </Tabs>
      </div>

      {/* O modo legado mantém senha manual; o temporário usa credenciais geradas pelo servidor. */}
      <Dialog open={showUserDialog} onClose={() => { if (!isSavingUser) { setShowUserDialog(false); resetUserForm(); } }} size="lg">
        <DialogHeader><DialogTitle>{dialogMode === 'create' ? 'Novo usuário' : dialogMode === 'edit' ? 'Editar usuário' : dialogMode === 'password' ? 'Alterar senha' : dialogMode === 'setup' ? 'Reenviar link para definir senha' : 'Reenviar acesso'}</DialogTitle></DialogHeader>
        <form onSubmit={handleUserSubmit} className={isDark ? 'text-gray-100' : 'text-slate-900'}>
          <div className="space-y-5">
            {dialogMode === 'setup' ? <p className="break-words text-sm">Um novo link para definir senha será enviado para {selectedUser?.email}, válido por 24 horas. O link anterior será invalidado.</p> : dialogMode === 'password' ? <p className="text-sm">Defina a nova senha de {selectedUser?.name}. As sessões desse usuário serão encerradas.</p> : dialogMode === 'resend' ? <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>Uma nova senha temporária será enviada para {selectedUser?.email}. O acesso temporário anterior será invalidado. Confirme apenas se o usuário precisa de um novo acesso.</p> : <>
              <section className={`space-y-4 rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-900/30' : 'border-slate-200 bg-slate-50'}`}>
                <h4 className="text-sm font-semibold">Dados do usuário</h4>
                {dialogMode === 'edit' && selectedUser?.identity_managed_by_user && <p role="status" className="text-sm">Esta clínica gerencia apenas as permissões e a equipe. A pessoa gerencia o próprio perfil e senha.</p>}
                {accessCapabilities?.enabled && <ProfilePhotoInput value={userForm.photo_url} onChange={photo_url => setUserForm(prev => ({ ...prev, photo_url }))} onReadingChange={setReadingUserPhoto} disabled={isSavingUser || (dialogMode === 'edit' && selectedUser?.identity_managed_by_user === true)} />}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Input label="Nome" aria-label="Nome do usuário" value={userForm.name} disabled={isSavingUser || (dialogMode === 'edit' && selectedUser?.identity_managed_by_user === true)} onChange={e => setUserForm({ ...userForm, name: e.target.value })} required />
                  <Input label="Email" aria-label="Email do usuário" type="email" value={userForm.email} disabled={isSavingUser || (dialogMode === 'edit' && selectedUser?.identity_managed_by_user === true)} onChange={e => setUserForm({ ...userForm, email: e.target.value })} required />
                </div>
                {dialogMode === 'edit' && selectedUser && userForm.email.trim().toLowerCase() !== selectedUser.email.trim().toLowerCase() && <p role="status" className="rounded-xl bg-sminy-primary/10 p-3 text-sm">Ao alterar o email, a senha e as sessões atuais serão invalidadas. {selectedUser.is_active === false ? 'Esta conta continuará desativada. Após a reativação, reenvie o link para o novo endereço.' : 'O novo endereço receberá um link para criar uma senha, válido por 24 horas.'}</p>}
                {accessCapabilities?.chat_signature_available === true && <label className="flex cursor-pointer items-start gap-3 text-sm">
                  <input type="checkbox" checked={userForm.show_name_in_chat} disabled={isSavingUser || (dialogMode === 'edit' && selectedUser?.identity_managed_by_user === true)} onChange={event => setUserForm(current => ({ ...current, show_name_in_chat: event.target.checked }))} className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 accent-sminy-primary focus:ring-2 focus:ring-sminy-primary disabled:cursor-not-allowed" />
                  <span>
                    <span className="block font-medium">Exibir nome nas mensagens enviadas pelo chat</span>
                    <span className={`mt-1 block text-xs ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Adiciona o nome deste usuário aos textos e legendas que ele enviar manualmente pelo chat.</span>
                  </span>
                </label>}
                <p className="text-sm">Clínica: {nameClinic || 'Clínica selecionada'}</p>
              </section>
              <section className={`space-y-4 rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-900/30' : 'border-slate-200 bg-slate-50'}`}>
                <div className="flex flex-wrap items-center justify-between gap-3"><h4 className="text-sm font-semibold">Equipe e acesso</h4><button type="button" disabled={isSavingUser} onClick={handleManageTeamsFromUserDialog} className="text-sm text-sminy-primary">Gerenciar equipes</button></div>
                <Select label="Equipe" aria-label="Equipe do usuário" disabled={isSavingUser} value={userForm.team_id?.toString() || ''} onChange={e => setUserForm({ ...userForm, team_id: e.target.value ? Number(e.target.value) : undefined })}>
                  <option value="">Sem equipe</option>
                  {teams.map(team => <option key={team.id} value={team.id}>{team.name}</option>)}
                </Select>
                {renderTeamAccessPreview(selectedTeamForUserForm)}
              </section>
              {dialogMode === 'create' && accessCapabilities?.enabled && <p className={`rounded-xl p-3 text-sm ${isDark ? 'bg-sminy-primary/10 text-gray-300' : 'bg-sminy-primary/5 text-slate-600'}`}>Uma conta nova receberá uma senha temporária por email, válida por 24 horas. Se o email já pertencer a uma conta de outra clínica, a pessoa receberá um convite por email e deverá aceitar em Meu perfil, sem alterar sua senha.</p>}
            </>}
            {accessCapabilities && !accessCapabilities.enabled && (dialogMode === 'create' || dialogMode === 'password') && <section className="grid gap-4 md:grid-cols-2">
              <Input label="Senha" aria-label="Senha" type="password" autoComplete="new-password" value={userForm.password} maxLength={72} disabled={isSavingUser} onChange={event => setUserForm(current => ({...current, password: event.target.value}))} required />
              <Input label="Confirmar senha" aria-label="Confirmar senha" type="password" autoComplete="new-password" value={userForm.confirm_password} maxLength={72} disabled={isSavingUser} onChange={event => setUserForm(current => ({...current, confirm_password: event.target.value}))} required />
            </section>}
            {(dialogMode === 'create' || dialogMode === 'resend') && accessCapabilities?.enabled && !accessCapabilities.email_delivery_available && <p role="status" className="text-sm text-amber-600">O serviço de email está indisponível. O cadastro pode ser salvo; o acesso precisará ser reenviado quando o serviço estiver disponível.</p>}
            {userOperationError && <p role="alert" className="text-sm text-red-500">{userOperationError}</p>}
          </div>
          <DialogFooter>
            <Button variant="secondary" type="button" disabled={isSavingUser} onClick={() => { setShowUserDialog(false); resetUserForm(); }}>Cancelar</Button>
            <Button variant="primary" type="submit" disabled={isSavingUser || readingUserPhoto}>{isSavingUser ? 'Salvando...' : dialogMode === 'create' ? 'Criar usuário' : dialogMode === 'edit' ? 'Salvar usuário' : dialogMode === 'password' ? 'Alterar senha' : 'Confirmar reenvio'}</Button>
          </DialogFooter>
        </form>
      </Dialog>

      <Dialog
        open={Boolean(userPendingDelete)}
        onClose={() => {
          if (!isDeletingUser) {
            setUserPendingDelete(null);
          }
        }}
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${
              isDark ? 'bg-red-500/15 text-red-300' : 'bg-red-50 text-red-600'
            }`}>
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle>Excluir usuário</DialogTitle>
              <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                {userPendingDelete ? `Você está removendo o acesso de ${userPendingDelete.name}.` : 'Confirme a exclusão do usuário.'}
              </p>
            </div>
          </div>
        </DialogHeader>
        <p className={`text-sm leading-6 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
          Esta ação remove o usuário da clínica. Ela não altera equipes nem permissões já configuradas para outros usuários.
        </p>
        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            disabled={isDeletingUser}
            onClick={() => setUserPendingDelete(null)}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={isDeletingUser}
            onClick={handleDeleteUser}
          >
            {isDeletingUser ? 'Excluindo...' : 'Excluir usuário'}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
};

export default ClinicConfig;
